import {
  APPLOVIN_ITEM_BRAND,
  APPLOVIN_ITEM_CATEGORY_ID,
  APPLOVIN_ITEM_IMAGE_URL,
  APPLOVIN_ITEM_LIST_ID,
} from '@features/Analytics/constants';

export const createTestSegmentFromQueryParameters = () => {
  const url = typeof window !== undefined ? window.location.href : '';
  const urlParameters = new URLSearchParams(new URL(url).search);

  const segmentName = urlParameters.get('segment_name');
  const testName = urlParameters.get('test_name');
  const caseName = urlParameters.get('case_name');

  if (segmentName && testName && caseName) {
    return {
      segment_name: decodeURI(segmentName),
      tests: [
        {
          test_name: decodeURI(testName),
          case_name: decodeURI(caseName),
        },
      ],
    };
  }

  return null;
};

type TpAppLovinPaymentType =
  | 'credit_card'
  | 'redeemable'
  | 'deferred'
  | 'payment_on_delivery'
  | 'wallet'
  | 'other';

export const createAppLovinUserData = ({
  userEmail,
  userId,
}: {
  userEmail?: string | null;
  userId?: string | number | null;
}) => {
  const email = userEmail?.trim().toLowerCase();
  const normalizedUserId = userId == null ? '' : String(userId).trim();
  const userData: { email?: string; user_id?: string } = {};

  if (email) {
    userData.email = email;
  }

  if (normalizedUserId && normalizedUserId !== '0') {
    userData.user_id = normalizedUserId;
  }

  return Object.keys(userData).length > 0 ? userData : undefined;
};

const getFiniteNumber = (rawValue?: string | number | null) => {
  if (rawValue == null || rawValue === '') {
    return undefined;
  }

  const numericValue =
    typeof rawValue === 'string' ? Number(rawValue) : rawValue;

  return Number.isFinite(numericValue) ? numericValue : undefined;
};

const getFirstFiniteNumber = (
  ...values: Array<string | number | null | undefined>
) => values.map(getFiniteNumber).find((value) => value != null);

export const getAppLovinActualPrice = ({
  fullPrice,
  fullPriceDiscount,
  introOfferData,
  isIntroOfferEnabled,
}: {
  fullPrice?: string | number | null;
  fullPriceDiscount?: string | number | null;
  introOfferData?: {
    [key: string]: any;
  } | null;
  isIntroOfferEnabled?: boolean;
}) => {
  const introOfferPrice = isIntroOfferEnabled
    ? getFirstFiniteNumber(
        introOfferData?.fullPriceDiscount,
        introOfferData?.fullPrice,
      )
    : undefined;

  if (introOfferPrice != null) {
    return introOfferPrice;
  }

  return getFirstFiniteNumber(fullPriceDiscount, fullPrice);
};

export const getAppLovinPaymentType = (
  paymentMethod?: string | null,
): TpAppLovinPaymentType | undefined => {
  if (!paymentMethod) {
    return undefined;
  }

  const normalizedPaymentMethod = paymentMethod
    .toLowerCase()
    .replace(/[^a-z]/g, '');

  if (
    normalizedPaymentMethod.includes('card') ||
    normalizedPaymentMethod.includes('credit')
  ) {
    return 'credit_card';
  }

  if (
    normalizedPaymentMethod.includes('applepay') ||
    normalizedPaymentMethod.includes('googlepay') ||
    normalizedPaymentMethod.includes('gpay') ||
    normalizedPaymentMethod.includes('paypal') ||
    normalizedPaymentMethod.includes('wallet')
  ) {
    return 'wallet';
  }

  if (
    normalizedPaymentMethod.includes('klarna') ||
    normalizedPaymentMethod.includes('afterpay') ||
    normalizedPaymentMethod.includes('deferred')
  ) {
    return 'deferred';
  }

  if (
    normalizedPaymentMethod.includes('gift') ||
    normalizedPaymentMethod.includes('redeem') ||
    normalizedPaymentMethod.includes('voucher')
  ) {
    return 'redeemable';
  }

  if (
    normalizedPaymentMethod.includes('cash') ||
    normalizedPaymentMethod.includes('delivery') ||
    normalizedPaymentMethod.includes('cod')
  ) {
    return 'payment_on_delivery';
  }

  return 'other';
};

export const createAppLovinEcommercePayload = ({
  currency,
  value,
  itemId,
  itemName,
  itemVariantId,
  transactionId,
  paymentMethod,
  paymentSystem,
  shipping,
  tax,
  userEmail,
  userId,
  variant,
}: {
  currency?: string | null;
  value?: string | number | null;
  itemId?: string | number | null;
  itemName?: string | null;
  itemVariantId?: string | number | null;
  transactionId?: string | number | null;
  paymentMethod?: string | null;
  paymentSystem?: string | null;
  shipping?: string | number;
  tax?: string | number;
  userEmail?: string | null;
  userId?: string | number | null;
  variant?: string | null;
}) => {
  const payload: { [key: string]: any } = {};
  const numericValue = getFiniteNumber(value);
  const numericShipping = getFiniteNumber(shipping);
  const numericTax = getFiniteNumber(tax);

  if (currency) {
    payload.currency = currency.toUpperCase();
  }

  if (numericValue != null) {
    payload.value = numericValue;
  }

  if (transactionId) {
    payload.transaction_id = String(transactionId);
    payload.shipping = numericShipping ?? 0;
    payload.tax = numericTax ?? 0;
  }

  if (paymentMethod) {
    payload.payment_type = getAppLovinPaymentType(paymentMethod);
  }

  if (paymentSystem) {
    payload.payment_system = paymentSystem;
  }

  if (variant) {
    payload.variant = variant;
  }

  const userData = createAppLovinUserData({ userEmail, userId });

  if (userData) {
    payload.user_data = userData;
  }

  if (itemId || itemName) {
    const appLovinItemId = String(itemId ?? itemName);
    const appLovinItemVariantId =
      itemVariantId != null
        ? String(itemVariantId)
        : `${appLovinItemId}:${variant ?? 'default'}`;
    const item: { [key: string]: string | number } = {
      affiliation: APPLOVIN_ITEM_BRAND,
      image_url: APPLOVIN_ITEM_IMAGE_URL,
      item_brand: APPLOVIN_ITEM_BRAND,
      item_category_id: APPLOVIN_ITEM_CATEGORY_ID,
      item_id: appLovinItemId,
      item_list_id: APPLOVIN_ITEM_LIST_ID,
      item_variant_id: appLovinItemVariantId,
      quantity: 1,
    };

    if (itemName) {
      item.item_name = itemName;
    }

    if (numericValue != null) {
      item.price = numericValue;
    }

    payload.items = [item];
  }

  return payload;
};

export const getAppLovinSubscriptionPlanPrice = (
  subscriptionPlan?: {
    fullPrice?: string | number | null;
    fullPriceDiscount?: string | number | null;
    fractionalPriceDiscount?: string | number | null;
    hasTrial?: boolean;
    introOfferData?: {
      [key: string]: any;
    } | null;
  } | null,
) => {
  if (!subscriptionPlan) {
    return undefined;
  }

  if (subscriptionPlan.hasTrial) {
    return getFiniteNumber(subscriptionPlan.fractionalPriceDiscount);
  }

  return getAppLovinActualPrice({
    fullPrice: subscriptionPlan.fullPrice,
    fullPriceDiscount: subscriptionPlan.fullPriceDiscount,
    introOfferData: subscriptionPlan.introOfferData,
    isIntroOfferEnabled: Boolean(subscriptionPlan.introOfferData),
  });
};

export const createAppLovinSubscriptionPlanPayload = ({
  subscriptionPlan,
  paymentMethod,
  paymentSystem,
  transactionId,
  userEmail,
  userId,
  variant,
}: {
  subscriptionPlan?: { [key: string]: any } | null;
  paymentMethod?: string | null;
  paymentSystem?: string | null;
  transactionId?: string | number | null;
  userEmail?: string | null;
  userId?: string | number | null;
  variant?: string | null;
}) =>
  createAppLovinEcommercePayload({
    currency: subscriptionPlan?.currency,
    value: getAppLovinSubscriptionPlanPrice(subscriptionPlan),
    itemId: subscriptionPlan?.braintreePlanId ?? subscriptionPlan?.id,
    itemName: subscriptionPlan?.name,
    transactionId,
    paymentMethod,
    paymentSystem,
    userEmail,
    userId,
    variant,
  });
